import { useState } from "react";
import { Plus, Minus } from "lucide-react";

const FAQS = [
  {
    q: "Do you offer free estimates?",
    a: "Yes — every project starts with a free on-site consultation and a transparent written estimate. We walk your space, measure the rooms, discuss materials, and send you a detailed quote within 24 hours.",
  },
  {
    q: "What flooring do you install?",
    a: "LVP (luxury vinyl plank), laminate, and tile. Kitchens, basements, bathrooms, and full-home installs across Greater St. Louis. Same crew from quote to final transition — level subfloor prep, tight seams, and finishes that hold up to real life.",
  },
  {
    q: "How much does flooring installation cost?",
    a: "Labor typically ranges from $3–$6 per square foot for LVP and laminate, and $7–$12 per square foot for tile — depending on subfloor prep, layout, transitions, and tile pattern complexity. We give a transparent line-item quote covering materials, prep, install, and finishing so there are no surprises.",
  },
  {
    q: "How long does a flooring install take?",
    a: "Most single-room LVP or laminate jobs finish in 1–2 days. Full-floor whole-home installs run 3–7 working days. Tile is slower due to setting and grouting time — typically 3–5 days for a bathroom, longer for larger areas. We give you a precise timeline before we start.",
  },
  {
    q: "What areas do you serve?",
    a: "All of St. Louis (city + county) and all of St. Charles County. Including St. Louis, Chesterfield, Clayton, Kirkwood, Ballwin, Ellisville, Wildwood, Creve Coeur, Florissant, Fenton, Webster Groves, St. Charles, O'Fallon, Wentzville, St. Peters, Lake St. Louis, Cottleville, Dardenne Prairie, Weldon Spring, and surrounding areas. Not sure if you're in our area? Call us at 636-849-6681.",
  },
  {
    q: "Are you licensed and insured?",
    a: "Yes — fully licensed and insured in Missouri. ICPI-certified installers. We're happy to share our insurance certificate on request. Family-owned and based locally since 2013.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);
  const toggle = (i) => setOpen(open === i ? -1 : i);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-16 lg:py-24 bg-stone-50"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 lg:mb-20">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-orange-600 mb-4">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full" />
              COMMON QUESTIONS
            </div>
            <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-900">
              Got questions?
              <br />
              <span className="text-stone-500">Real answers.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-stone-600 leading-relaxed text-lg">
              The questions we hear most from St. Louis homeowners before
              starting a paver, patio, or walkway project. Don't see yours?
              Call us — 636-849-6681.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={f.q}
                data-testid={`faq-item-${i}`}
                className={`border-b border-stone-200 ${
                  i === 0 ? "border-t" : ""
                }`}
              >
                <button
                  onClick={() => toggle(i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-start justify-between gap-6 py-6 lg:py-7 text-left group"
                  aria-expanded={isOpen}
                >
                  <span className="font-display font-semibold text-xl md:text-2xl text-stone-900 leading-snug">
                    {f.q}
                  </span>
                  <span
                    className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      isOpen
                        ? "bg-orange-500 text-stone-50"
                        : "bg-stone-100 text-stone-900 group-hover:bg-stone-200"
                    }`}
                  >
                    {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                  </span>
                </button>
                <div
                  className={`grid transition-all duration-400 ease-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100 pb-7" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="text-stone-600 text-[15px] md:text-base leading-relaxed pr-12 md:pr-16 max-w-3xl">
                      {f.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
